import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useRef } from "react";
import { CustomToastComponent } from "@/components/shared";
import { client } from "@/client";
import { useMutation } from "@apollo/client/react";
import {
  RebalancePurchaseRatiosMutation,
  RebalancePurchaseRatiosMutationVariables,
  RebalancePurchaseRatiosDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
  FetchPurchasesOnTenderDocument,
} from "@/graphql/__generated__/graphql";

interface RebalancePurchaseRatiosOptions {
  disableToast?: boolean;
}

/**
 * Custom hook for rebalancing purchase service ratios on a tender.
 *
 * @param {RebalancePurchaseRatiosOptions} options - Options for the hook.
 * @returns {Object} - Contains the rebalancePurchaseRatiosMutation function, loading state, error state, and setDisableToast function.
 */
export default function useRebalancePurchaseRatiosMutationHook(options: RebalancePurchaseRatiosOptions = {}): {
  rebalancePurchaseRatiosMutation: ReturnType<typeof useMutation<RebalancePurchaseRatiosMutation, RebalancePurchaseRatiosMutationVariables>>[0];
  loading: boolean;
  error: ReturnType<typeof useMutation<RebalancePurchaseRatiosMutation, RebalancePurchaseRatiosMutationVariables>>[1]["error"];
  setDisableToast: (_value: boolean) => void;
  } {
  const { disableToast: initialDisableToast = false } = options;
  const disableToastRef = useRef(initialDisableToast);
  const setDisableToast = (value: boolean) => {
    disableToastRef.current = value;
  };

  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [rebalancePurchaseRatiosMutation, { loading, error }] = useMutation<
    RebalancePurchaseRatiosMutation,
    RebalancePurchaseRatiosMutationVariables
  >(RebalancePurchaseRatiosDocument, {
    onCompleted: () => {
      if (!disableToastRef.current) {
        toast(CustomToastComponent, {
          toastId: "purchase-ratios-updated-success",
          data: {
            title: t("toasts.purchaseratiosupdated"),
            content: t("toasts.purchaseratiosupdatedsuccessfully"),
            type: "success",
          },
        });
      }

      // Refetch only active FetchServicesOnTender queries for this tender
      // This ensures we make only ONE network call instead of multiple
      // for different parameter combinations
      client.refetchQueries({
        include: [FetchServicesOnTenderDocument],
        onQueryUpdated(observableQuery) {
          // Only refetch queries for this specific tender
          const variables = observableQuery.options.variables as { tenderId?: number } | undefined;
          return variables?.tenderId === Number(tenderID);
        },
      });
    },
    onError: () => {
      toast(CustomToastComponent, {
        toastId: "purchase-ratios-updated-error",
        data: {
          title: t("toasts.error"),
          content: t("toasts.purchaseratiosupdatederror"),
          type: "error",
        },
      });
    },
    refetchQueries: [
      {
        query: FetchTenderByIdDocument,
        variables: { id: Number(tenderID) },
      },
      {
        query: FetchPurchasesOnTenderDocument,
        variables: { tenderId: Number(tenderID) },
      },
    ],
  });

  return {
    rebalancePurchaseRatiosMutation,
    loading,
    error,
    setDisableToast,
  };
}
